/**
 * PostHog browser-side bootstrap. Next.js 15.3+ runs this file once, in the
 * browser, before any client component renders — lighter than the older
 * `<PostHogProvider>` pattern from app/layout.tsx.
 *
 * Empty key is a safe no-op: `posthog.init` is gated, so dev environments
 * without a key load cleanly. The capture surface (autocapture, pageviews,
 * errors) is left at PostHog's defaults so this single file delivers both
 * analytics and error tracking with no extra wiring.
 *
 * Disabled in development by default: we DON'T call `init()` at all unless
 * we're in production OR `NEXT_PUBLIC_POSTHOG_DEV=1` is set. Opting out via
 * the old `loaded` callback was too late — `init()` already fires a `/flags`
 * request and queues events before `loaded` runs, which (when the PostHog host
 * is unreachable) floods the dev proxy with ETIMEDOUT retries. Not initializing
 * is the only way to keep dev fully silent.
 *
 * The `api_host: '/ingest'` value is rewritten to the real POSTHOG_HOST by
 * next.config.mjs so ad blockers don't drop events. `ui_host` keeps the
 * "view in PostHog" links pointing at the real product URL.
 */
import posthog from 'posthog-js';

import { ANALYTICS_OPTOUT_COOKIE, analyticsOptOutFromCookieValue } from '@/lib/analytics/cookies';

/**
 * Read the consent cookie straight from document.cookie. This has to happen
 * before init(): as the note above records, opting out in the `loaded` callback
 * was too late, because init() already fires a /flags request and queues events
 * before `loaded` runs. The same reasoning applies to a refusal.
 */
function analyticsOptedOut(): boolean {
  if (typeof document === 'undefined') return false;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${ANALYTICS_OPTOUT_COOKIE}=`));
  return analyticsOptOutFromCookieValue(match?.split('=')[1]);
}

const optedOut = analyticsOptedOut();

const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const uiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';
// Set NEXT_PUBLIC_POSTHOG_DEV=1 to exercise PostHog locally on purpose.
const enabledInDev = process.env.NEXT_PUBLIC_POSTHOG_DEV === '1';

if (!optedOut && key && (process.env.NODE_ENV === 'production' || enabledInDev)) {
  posthog.init(key, {
    api_host: '/ingest',
    ui_host: uiHost,
    // Snapshot of safe modern defaults (autocapture, pageviews, exceptions on,
    // session replay off). Pinning a defaults date keeps behaviour stable
    // across PostHog SDK upgrades — change deliberately when you opt into
    // new behaviour.
    defaults: '2026-01-30',
  });
}
